import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, X, Loader2 } from "lucide-react";
import CompanyAdminLayout from "../../layouts/CompanyAdminLayout";
import { useToast } from "../../components/ToastContext";
import {
  createTeam,
  getTeamById,
  getTeams,
  getCurrentUser,
  getOrganizationMembers,
  addTeamMembers,
  removeTeamMember,
} from "../../api/services/companyAdminService";
import TeamDetailPanel from "../../components/CompanyAdmin/Teamdetailpanel";
import TeamsSidebar from "../../components/CompanyAdmin/TeamsSideba";

const ACCENT_POOL = [
  "#6366f1",
  "#0ea5e9",
  "#f97316",
  "#8b5cf6",
  "#f43f5e",
  "#06b6d4",
  "#a16207",
  "#0891b2",
];

function getAccent(index) {
  return ACCENT_POOL[index % ACCENT_POOL.length];
}

export default function TeamsManagement() {
  const { showToast } = useToast();

  const [teams, setTeams] = useState([]);
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const [isLoadingOrgMembers, setIsLoadingOrgMembers] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [isLoadingTeamDetails, setIsLoadingTeamDetails] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // ── Accent map ──
  const teamAccentMap = useMemo(() => {
    const map = {};
    teams.forEach((t, i) => {
      map[t.id] = getAccent(i);
    });
    return map;
  }, [teams.map((t) => t.id).join(",")]);

  const selectedTeam = useMemo(
    () => teams.find((t) => t.id === selectedTeamId) ?? null,
    [teams, selectedTeamId],
  );

  const selectedAccent = selectedTeam
    ? teamAccentMap[selectedTeam.id]
    : "#6366f1";

  const totalMembers = teams.reduce(
    (sum, t) => sum + (t.totalMembers ?? t.memberIds?.length ?? 0),
    0,
  );

  // ── Fetch team details ──
  const fetchTeamDetails = useCallback(
    async (teamId) => {
      setIsLoadingTeamDetails(true);
      try {
        const data = await getTeamById(teamId);
        if (!data) return;
        const members = Array.isArray(data.members)
          ? data.members.map((m) => ({
              id: m.id || m.user_id || m.username,
              user_id: m.user_id,
              name: m.full_name || m.name || m.username || "Member",
              title: m.designation || m.title || m.role || "Member",
            }))
          : [];
        setTeams((prev) =>
          prev.map((team) =>
            team.id === teamId
              ? {
                  ...team,
                  name: data.name || team.name,
                  totalMembers: data.total_members ?? team.totalMembers,
                  createdByName: data.created_by_name || team.createdByName,
                  createdAt: data.created_at || team.createdAt,
                  members,
                }
              : team,
          ),
        );
      } catch (error) {
        showToast(error.message || "Failed to load team details", "error");
      } finally {
        setIsLoadingTeamDetails(false);
      }
    },
    [showToast],
  );

  // ── Initialize on mount ──
  useEffect(() => {
    const initialize = async () => {
      setTeams([]);
      setSelectedTeamId(null);
      setOrganizationMembers([]);
      setIsLoadingTeams(true);

      try {
        const data = await getTeams({ page: 1, pageSize: 100 });
        const items = Array.isArray(data?.teams) ? data.teams : [];
        const mapped = items.map((team) => ({
          id: team.id,
          name: team.name,
          description: "",
          leadId: null,
          memberIds: [],
          members: [],
          totalMembers: team.total_members ?? 0,
          createdByName: team.created_by_name || "",
          createdAt: team.created_at || "",
        }));
        setTeams(mapped);
      } catch (error) {
        showToast(error.message || "Failed to load teams", "error");
      } finally {
        setIsLoadingTeams(false);
      }

      try {
        setIsLoadingOrgMembers(true);
        const me = await getCurrentUser();
        const orgId = me?.organization_id;
        if (orgId) {
          const orgData = await getOrganizationMembers(orgId, {
            page: 1,
            pageSize: 100,
          });
          setOrganizationMembers(
            Array.isArray(orgData?.members) ? orgData.members : [],
          );
        }
      } catch (error) {
        showToast(
          error.message || "Failed to load organization members",
          "error",
        );
      } finally {
        setIsLoadingOrgMembers(false);
      }
    };

    initialize();
  }, [showToast]);

  // ── Handlers ──
  const handleTeamSelect = (teamId) => {
    setSelectedTeamId(teamId);
    fetchTeamDetails(teamId);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setIsCreating(true);
    try {
      const response = await createTeam({ name: newTeamName.trim() });
      const newId = response?.id || Date.now();
      const newEntry = {
        id: newId,
        name: newTeamName.trim(),
        description: "",
        leadId: null,
        memberIds: [],
        members: [],
        totalMembers: 0,
        createdByName: "",
        createdAt: "",
      };
      setTeams((prev) => [...prev, newEntry]);
      setNewTeamName("");
      setIsCreateOpen(false);
      showToast("Team created successfully", "success");
    } catch (error) {
      showToast(error.message || "Failed to create team", "error");
    } finally {
      setIsCreating(false);
    }
  };

  // Passed down to TeamDetailPanel as onMemberAdded
  const handleMemberAdded = async (teamId, draftMemberIds) => {
    const response = await addTeamMembers(teamId, draftMemberIds);

    const added = response?.added ?? 0;
    const skipped = response?.skipped ?? 0;
    const results = Array.isArray(response?.results) ? response.results : [];

    const skippedInAnotherTeam = results.filter(
      (r) => r.status === "already_in_another_team",
    ).length;
    const skippedAlreadyMember = results.filter(
      (r) => r.status === "already_member",
    ).length;

    const parts = [];
    if (added > 0)
      parts.push(
        `Added ${added} member${added !== 1 ? "s" : ""} successfully.`,
      );
    if (skippedInAnotherTeam > 0)
      parts.push(
        `Skipped ${skippedInAnotherTeam} user${skippedInAnotherTeam !== 1 ? "s" : ""} — already in another team.`,
      );
    if (skippedAlreadyMember > 0)
      parts.push(
        `Skipped ${skippedAlreadyMember} user${skippedAlreadyMember !== 1 ? "s" : ""} — already in this team.`,
      );
    if (added === 0 && skipped > 0 && parts.length === 1)
      parts.push(`Skipped ${skipped} user${skipped !== 1 ? "s" : ""}.`);

    const message = parts.join(" ") || "No changes made.";
    showToast(message, added > 0 ? "success" : "error");
    await fetchTeamDetails(teamId);
  };

  // Passed down to TeamDetailPanel as onMemberRemoved
  const handleMemberRemoved = async (teamId, member) => {
    const userId = member.user_id || member.id;
    await removeTeamMember(teamId, userId);
    showToast(`${member.name} removed from team`, "success");
    await fetchTeamDetails(teamId);
  };

  return (
    <CompanyAdminLayout>
      {/* Full-height flex column */}
      <div className="flex flex-col" style={{ height: "calc(100vh - 64px)" }}>
        {/* ── Page Header ── */}
        <div className="px-4 sm:px-8 py-6 sm:py-8 border-b border-(--color-border) flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between shrink-0">
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <h1 className="text-2xl font-bold text-(--color-text-primary)">
                Teams Management
              </h1>
              <button
                onClick={() => setIsCreateOpen(true)}
                className="sm:hidden flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white dark:text-black bg-(--color-primary) hover:opacity-90 transition-all shadow-lg whitespace-nowrap shrink-0 mt-0.5"
              >
                <Plus className="w-3 h-3" /> New Team
              </button>
            </div>
            <p className="text-xs text-(--color-text-muted) mt-0.5">
              Create teams, manage membership, and control ownership.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="hidden md:flex items-center divide-x divide-(--color-border) border border-(--color-border) rounded-lg overflow-hidden bg-(--color-bg-card)">
              {[
                { label: "Teams", value: teams.length },
                { label: "Members", value: totalMembers },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col items-center px-5 py-2.5"
                >
                  <span className="text-base font-black text-(--color-text-primary) leading-none">
                    {s.value}
                  </span>
                  <span className="text-[10px] text-(--color-text-muted) mt-0.5 font-medium">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setIsCreateOpen(true)}
              className="hidden sm:flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold text-white dark:text-black bg-(--color-primary) hover:opacity-90 transition-all shadow-lg shrink-0"
            >
              <Plus className="w-4 h-4" /> New Team
            </button>
          </div>
        </div>

        {/* ── Split layout (sidebar + detail) ── */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* SIDEBAR — full screen on mobile when no team selected, fixed column on desktop */}
          <div
            className={`
            flex-col overflow-hidden
            w-full md:w-56 md:flex md:shrink-0 md:border-r md:border-(--color-border) md:bg-(--color-card)
            ${selectedTeamId ? "hidden md:flex" : "flex"}
          `}
          >
            {/* Mobile header */}
            <div className="md:hidden px-4 pt-4 pb-2 shrink-0">
              <p className="text-[9px] font-black uppercase text-(--color-text-muted)">
                All Teams
              </p>
            </div>

            <TeamsSidebar
              teams={teams}
              selectedTeamId={selectedTeamId}
              teamAccentMap={teamAccentMap}
              isLoading={isLoadingTeams}
              onTeamSelect={handleTeamSelect}
            />
          </div>

          {/* DETAIL PANEL — full screen on mobile when a team is selected, flex-1 on desktop */}
          <div
            className={`
            flex-col flex-1 overflow-hidden min-h-0
            ${selectedTeamId ? "flex" : "hidden md:flex"}
          `}
          >
            <TeamDetailPanel
              team={selectedTeam}
              accent={selectedAccent}
              isLoadingDetails={isLoadingTeamDetails}
              orgMembers={organizationMembers}
              isLoadingOrgMembers={isLoadingOrgMembers}
              onMemberAdded={handleMemberAdded}
              onMemberRemoved={handleMemberRemoved}
              onBack={() => setSelectedTeamId(null)}
            />
          </div>
        </div>
      </div>

      {/* ── Create Team Modal ── */}
      {isCreateOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-(--color-bg)/50 backdrop-blur-md rounded-md shadow-2xl w-full max-w-2xl border border-(--color-border) overflow-hidden">
            <div className="flex items-center justify-between px-5 sm:px-8 py-5 sm:py-6 border-b border-(--color-border)">
              <div>
                <h2 className="text-base font-bold text-(--color-text-primary)">
                  Create New Team
                </h2>
                <p className="text-sm text-(--color-text-muted) mt-1">
                  Give your team a name to get started.
                </p>
              </div>
              <button
                onClick={() => setIsCreateOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-(--color-text-muted) hover:bg-(--color-bg-overlay) transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="px-5 sm:px-8 py-6 sm:py-8">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-(--color-text-muted)">
                    Team Name
                  </label>
                  <input
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder="e.g. Threat Intel"
                    required
                    className="px-4 py-3 text-sm border border-(--color-border) rounded-lg bg-(--color-bg-card) text-(--color-text-primary) placeholder:text-(--color-text-muted)/40 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30 focus:border-(--color-primary) transition-all"
                  />
                </div>
              </div>
              <div className="px-5 sm:px-8 py-5 border-t border-(--color-border) flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  disabled={isCreating}
                  className="flex-1 py-3 border border-(--color-border) text-(--color-text-primary) rounded-lg hover:bg-(--color-bg-overlay) transition-colors text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 py-3 bg-(--color-primary) text-white dark:text-black rounded-lg hover:opacity-90 transition-all text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                      Creating...
                    </>
                  ) : (
                    "Create Team"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CompanyAdminLayout>
  );
}
